/**
 * conversationVoiceOutbox — les vocaux de la file d'envoi, vus comme des envois de la conversation.
 *
 * Le fil mêle ce qui est lu et ce que l'élève envoie d'ici (`ConversationOutboxEntry`, la boîte
 * d'envoi de l'écran). Un vocal confié à la file en devient un, pour son fil seulement :
 *  - à envoyer ou en cours → « Envoi... » ;
 *  - en échec → « Non envoyé » et « Renvoyer », qui le remet dans la file ;
 *  - envoyé → la forme que le serveur a rangée (sa copie lue plus tard dans le fil est écartée).
 * La bulle se lit tout de suite, sur le fichier local.
 */
import { type ConversationOutboxEntry } from "../../../../view-models/student/Messaging/studentTutorConversationViewModel"
import { formatVoiceDuration } from "../../../formatters/voiceFormatter"
import { type MessengerVoiceItem } from "./messengerVoiceOutbox"

const STATUS: Record<MessengerVoiceItem["status"], ConversationOutboxEntry["status"]> = {
    pending: "sending",
    sending: "sending",
    failed: "failed",
    sent: "sent",
}

export function voiceOutboxEntries(items: MessengerVoiceItem[], threadId: string | null): ConversationOutboxEntry[] {
    if (threadId == null) return []
    return items
        .filter((item) => item.target.threadId === threadId)
        .map((item) => ({
            localId: item.id,
            sentAt: item.createdAt,
            attachment: {
                kind: "voice" as const,
                uri: item.file.uri,
                durationLabel: formatVoiceDuration(item.file.durationMs / 1000),
            },
            status: STATUS[item.status],
            ...(item.status === "sent" && item.result != null ? { server: item.result } : {}),
        }))
}

/** La boîte d'envoi de l'écran et les vocaux de la file, dans l'ordre des gestes. */
export function mergeOutboxes(
    screen: ConversationOutboxEntry[],
    voices: ConversationOutboxEntry[]
): ConversationOutboxEntry[] {
    if (voices.length === 0) return screen
    return [...screen, ...voices].sort((a, b) => a.sentAt.localeCompare(b.sentAt))
}
